import { useRef } from "react";
import { motion, useInView } from "framer-motion";
// react-icons/fa imports removed — icons below come from ri/md/gi
import "./WhoWeServe.css";
import { RiPoliceBadgeLine } from "react-icons/ri";
import { MdOutlineMilitaryTech, MdStarBorderPurple500 } from "react-icons/md";
import { GiFireShield } from "react-icons/gi";
import CircularBadge from "../badge";

/* ── Card data ─────────────────────────────────────────────── */
const CARDS = [
  {
    id: "veterans",
    img: "./assets/vet.png",
    icon: MdOutlineMilitaryTech,
    title: "Veterans",
    body: "You came home different, and the system handed you a prescription instead of a path forward.",
  },
  {
    id: "first-responders",
    img: "./assets/vet1.png",
    icon: GiFireShield,
    title: "First Responders",
    body: "Fire, EMS, paramedics, you carry what you've seen so others don't have to.",
  },
  {
    id: "law-enforcement",
    img: "./assets/vet2.png",
    icon: RiPoliceBadgeLine,
    title: "Law Enforcement",
    body: "You signed up to protect people, not to carry every call home with you.",
  },
  {
    id: "gold-star",
    img: "./assets/vet3.png",
    icon: MdStarBorderPurple500,
    title: "Gold Star Families",
    body: "You lost a child, a sibling, a spouse, a parent, and that loss is its own kind of war.",
  },
];

/* ── Animation variants ────────────────────────────────────── */
const ease = [0.22, 1, 0.36, 1];

const fadeUp = (delay = 0) => ({
  hidden: { opacity: 0, y: 28 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease, delay },
  },
});

const cardVariant = (i) => ({
  hidden: { opacity: 0, y: 40 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease, delay: 0.5 + i * 0.1 },
  },
});

/* ── Circular badge (pure CSS + SVG text path) ─────────────── */

/* ── Mountain / forest SVG divider ─────────────────────────── */
function MountainDivider() {
  return (
    <div className="wws__divider d-flex justify-content-center gap-2 align-items-center" aria-hidden="true">
      <span className="border-bottom-div " /> <img src="./assets/mountainsForst.png" alt="" /> <span className="border-bottom-div" />
    </div>
  );
}

/* ── Main component ─────────────────────────────────────────── */
export default function WhoWeServe() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section className="wws" id="who-we-serve" ref={ref}>
      <div className="container-fg container-2xxl">
        {/* ── Header row ── */}
        <div className="wws__header">
          {/* Left: section label + headline */}
          <div className="wws__header-left">
            <motion.div className="wws__section-label" variants={fadeUp(0)} initial="hidden" animate={inView ? "show" : "hidden"}>
              <span className="wws__num">02</span>
              <span className="wws__rule" aria-hidden="true" />
              <span className="u-eyebrow">Who This Is For</span>
            </motion.div>

            <motion.h2 className="wws__headline u-display" variants={fadeUp(0.1)} initial="hidden" animate={inView ? "show" : "hidden"}>
              We Walk
              <br />
              With You.
            </motion.h2>
          </div>

          {/* Centre: body copy */}
          <motion.p className="wws__header-copy" variants={fadeUp(0.18)} initial="hidden" animate={inView ? "show" : "hidden"}>
            Forge serves those who've carried more than most should have to. Different battles, same weight. You're not alone and you don't have to carry it here.
          </motion.p>

          {/* Right: circular badge */}
          <motion.div className="wws__badge-wrap" variants={fadeUp(0.25)} initial="hidden" animate={inView ? "show" : "hidden"}>
            <CircularBadge colors="var(--c-sage)" />
          </motion.div>
        </div>
      </div>
      <div className="row mx-0 px-3 px-lg-5">
        {/* ── Four cards ── */}
        <div className="wws__grid">
          <motion.article className="wws__card plant" variants={cardVariant(0)} initial="hidden" animate={inView ? "show" : "hidden"}>
            <img src="./assets/plant.png" alt="" />
          </motion.article>

          {CARDS.map((card, i) => {
            const Icon = card.icon;
            return (
              <motion.article key={card.id} className="wws__card" variants={cardVariant(i)} initial="hidden" animate={inView ? "show" : "hidden"}>
                {/* Photo with torn-paper bottom edge */}
                <div className="wws__card-img-wrap">
                  <img src={card.img} alt={card.title} className="wws__card-img" loading="lazy" />
                  {/* Torn-edge mask — SVG clipPath + irregular bottom */}
                  <div className={`wws__card-tear ${i % 2 === 0 ? "flip" : ""}`} aria-hidden="true">
                    <img src="./assets/tear.svg" alt="" />
                  </div>
                </div>

                {/* Card body */}
                <div className="wws__card-body">
                  <div className="wws__card-icon" aria-hidden="true">
                    <Icon />
                  </div>
                  <h3 className="wws__card-title u-display">{card.title}</h3>
                  <p className="wws__card-text">{card.body}</p>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>

      {/* ── Mountain / forest divider ── */}
      <MountainDivider />
    </section>
  );
}
